import { faTriangleExclamation, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { Link } from 'react-router-dom';

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Typography
} from '@/ui';
import { titleize } from '@/utils/titleize';

import NonBillableBillingItem from '../NonBillableBillingItem';

import type { Props } from './types';

/**
 * Billing as a counselor needs it: whether the encounter is billable, a way
 * into the detail, and the alerts out of the way behind a button. The full
 * breakdown lives on the billing tab, where billing staff work it.
 */
const EncounterBillingCard = ({ billingItem, encounterType }: Props) => {
  const alerts = billingItem?.active_detected_alerts ?? [];

  return (
    <div className="mt-2 flex flex-wrap items-center gap-1">
      <NonBillableBillingItem billingItem={billingItem} compact encounterType={encounterType} />

      <Link to="billing">
        <Button className="whitespace-nowrap" color="secondary" size="xs">
          View Details
        </Button>
      </Link>

      {!!alerts.length && (
        <DropdownMenu>
          <DropdownMenuTrigger>
            <Button className="whitespace-nowrap" color="destructive" size="xs">
              All Errors ({alerts.length})
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="start" className="max-w-sm">
            {alerts.map((alert) => (
              <DropdownMenuItem as="div" key={alert.id}>
                <FontAwesomeIcon
                  className={
                    alert.level === 'error'
                      ? 'text-uniform-red-600 dark:text-uniform-red-500'
                      : 'text-uniform-yellow-600 dark:text-uniform-yellow-500'
                  }
                  icon={alert.level === 'error' ? faXmark : faTriangleExclamation}
                />

                <span>
                  <Typography as="span" variant="caption" weight="semibold">
                    {titleize(alert.group)}
                  </Typography>{' '}
                  <Typography as="span" muted variant="caption">
                    {alert.description}
                  </Typography>
                </span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
};

export default React.memo(EncounterBillingCard);
